/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Layout from '../../bootstrap/Layout';
import Container from '../../bootstrap/Container';
import Spinner from '../../bootstrap/Spinner';
import {
  setPendingVerifications,
  setVerificationLoading,
  setVerificationError,
  verifyPaymentSuccess,
  rejectPaymentSuccess,
  setKasirUserInfo,
} from '../../redux/action/paymentVerificationAction';
import {
  getPendingVerifications,
  getApprovedBookings,
  getAwaitingPelunasan,
  uploadPaymentProof,
  verifyPayment,
  rejectPaymentProof,
} from '../../helpers/paymentVerificationAPI';
import {
  DashboardWrapper,
  DashboardHeader,
  Title,
  Subtitle,
  StatsRow,
  StatCard,
  StatLabel,
  StatValue,
  TableContainer,
  TableHeader,
  TableTitle,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableCell,
  StatusBadge,
  ActionButtons,
  Button,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  LoadingContainer,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  FormGroup,
  Label,
  TextArea,
  ModalFooter,
  CancelButton,
  SubmitButton,
  AmountText,
  BookingLink,
} from './KasirDashboard.styles';

const KasirDashboard = () => {
  const dispatch = useDispatch();
  const { pendingVerifications, loading, error, userRole, canAccess } = useSelector(
    (state) => state.paymentVerification
  );

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // State for approved bookings and upload
  const [approvedBookings, setApprovedBookings] = useState([]);
  const [approvedLoading, setApprovedLoading] = useState(false);

  // State for awaiting pelunasan bookings
  const [awaitingPelunasan, setAwaitingPelunasan] = useState([]);
  const [pelunasanLoading, setPelunasanLoading] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [uploadData, setUploadData] = useState({
    payment_amount: '',
    payment_method: 'transfer',
    payment_type: 'dp',
    notes: '',
    payment_proof: null,
  });

  useEffect(() => {
    // Check if auth_token is in URL (from backend redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const authToken = urlParams.get('auth_token');

    if (authToken) {
      try {
        // Decode base64 token
        const userData = JSON.parse(atob(authToken));

        // Only Kasir can access this dashboard
        let canAccessDashboard = false;
        const roleLower = userData.role?.toLowerCase() || '';

        if (roleLower.includes('kasir')) {
          canAccessDashboard = true;
        }
        // Other roles (Manager, Supervisor, Admin, etc.) cannot access

        // Set user info in Redux
        dispatch(setKasirUserInfo({
          ...userData,
          can_access: canAccessDashboard,
        }));

        // Clean URL (remove token)
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (err) {
        console.error('Failed to decode auth token:', err);
      }
    }

    fetchPendingVerifications();
    fetchApprovedBookings();
    fetchAwaitingPelunasan();
  }, []);

  const fetchPendingVerifications = async () => {
    try {
      dispatch(setVerificationLoading(true));
      const response = await getPendingVerifications();

      if (response.status === 'success') {
        dispatch(setPendingVerifications(response.data || []));
      } else {
        throw new Error(response.message || 'Gagal mengambil data');
      }
    } catch (err) {
      dispatch(setVerificationError(err.message || 'Gagal mengambil data verifikasi'));
      toast.error(err.message || 'Gagal mengambil data verifikasi');
    }
  };

  const fetchApprovedBookings = async () => {
    try {
      setApprovedLoading(true);
      const response = await getApprovedBookings();

      if (response.status === 'success') {
        setApprovedBookings(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching approved bookings:', err);
    } finally {
      setApprovedLoading(false);
    }
  };

  const fetchAwaitingPelunasan = async () => {
    try {
      setPelunasanLoading(true);
      const response = await getAwaitingPelunasan();

      if (response.status === 'success') {
        setAwaitingPelunasan(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching awaiting pelunasan:', err);
    } finally {
      setPelunasanLoading(false);
    }
  };

  const handleUploadClick = (booking, isPelunasan = false) => {
    setSelectedBooking(booking);

    // Auto-fill dengan sisa pembayaran jika pelunasan
    const defaultAmount = isPelunasan
      ? booking.remaining_amount
      : (booking.dp_amount || booking.contract_price || '');

    setUploadData({
      payment_amount: defaultAmount,
      payment_method: 'transfer',
      payment_type: isPelunasan ? 'pelunasan' : 'dp',
      notes: isPelunasan ? 'Pembayaran Pelunasan' : '',
      payment_proof: null,
    });
    setShowUploadModal(true);
  };

  const handleUploadSubmit = async () => {
    if (!uploadData.payment_proof) {
      toast.error('Bukti pembayaran harus diupload');
      return;
    }

    if (!uploadData.payment_amount || parseFloat(uploadData.payment_amount) <= 0) {
      toast.error('Jumlah pembayaran harus diisi');
      return;
    }

    try {
      setActionLoading(true);
      const formData = new FormData();
      formData.append('payment_amount', uploadData.payment_amount);
      formData.append('payment_method', uploadData.payment_method);
      formData.append('payment_type', uploadData.payment_type);
      formData.append('notes', uploadData.notes);
      formData.append('payment_proof', uploadData.payment_proof);

      const response = await uploadPaymentProof(selectedBooking.id, formData);

      if (response.status === 'success') {
        toast.success('✅ Bukti pembayaran berhasil diupload!', { autoClose: 3000 });
        // Show reminder to verify
        setTimeout(() => {
          toast.info('⚠️ Jangan lupa VERIFIKASI pembayaran di tabel atas!', {
            autoClose: 5000,
            style: { background: '#f39c12', color: 'white' }
          });
        }, 500);
        setShowUploadModal(false);
        setSelectedBooking(null);
        // Refresh all lists
        fetchApprovedBookings();
        fetchAwaitingPelunasan();
        fetchPendingVerifications();
        // Scroll to top to show pending verification
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error(response.message || 'Gagal upload');
      }
    } catch (err) {
      toast.error(err.message || 'Gagal upload bukti pembayaran');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseUploadModal = () => {
    if (!actionLoading) {
      setShowUploadModal(false);
      setSelectedBooking(null);
    }
  };

  const handleVerify = async (verificationId) => {
    if (!window.confirm('Yakin ingin verifikasi pembayaran ini?')) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await verifyPayment(verificationId);

      if (response.status === 'success') {
        dispatch(verifyPaymentSuccess({ verification_id: verificationId }));
        toast.success(response.message || 'Pembayaran berhasil diverifikasi!');

        // Refresh all lists
        fetchPendingVerifications();
        fetchAwaitingPelunasan();
      } else {
        throw new Error(response.message || 'Gagal verifikasi');
      }
    } catch (err) {
      toast.error(err.message || 'Gagal verifikasi pembayaran');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = (verification) => {
    setSelectedVerification(verification);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Alasan penolakan harus diisi');
      return;
    }

    try {
      setActionLoading(true);
      const response = await rejectPaymentProof(
        selectedVerification.id,
        rejectionReason
      );

      if (response.status === 'success') {
        dispatch(rejectPaymentSuccess({ verification_id: selectedVerification.id }));
        toast.success('Bukti pembayaran ditolak');
        setShowRejectModal(false);
        setSelectedVerification(null);
        setRejectionReason('');
      } else {
        throw new Error(response.message || 'Gagal reject');
      }
    } catch (err) {
      toast.error(err.message || 'Gagal reject bukti pembayaran');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseModal = () => {
    if (!actionLoading) {
      setShowRejectModal(false);
      setSelectedVerification(null);
      setRejectionReason('');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const stats = {
    pending: pendingVerifications.length,
    totalAmount: pendingVerifications.reduce((sum, v) => sum + parseFloat(v.payment_amount || 0), 0),
  };

  // Check if user has permission to access this dashboard
  if (canAccess === false) {
    return (
      <Layout>
        <Container>
          <DashboardWrapper>
            <DashboardHeader>
              <div>
                <Title>Dashboard Kasir</Title>
                <Subtitle style={{ color: '#e74c3c' }}>
                  Akses Ditolak
                </Subtitle>
              </div>
            </DashboardHeader>
            <EmptyState>
              <EmptyStateIcon style={{ color: '#e74c3c' }}>⛔</EmptyStateIcon>
              <EmptyStateText>
                Anda tidak memiliki izin untuk mengakses Kasir Dashboard.
                <br />
                <small style={{ color: '#7f8c8d' }}>
                  Role Anda: <strong>{userRole || 'Unknown'}</strong>
                  <br />
                  Hanya Kasir yang dapat mengakses halaman ini.
                </small>
              </EmptyStateText>
              <Button
                variant="secondary"
                onClick={() => window.location.href = '/modules/backend/booking/dashboard'}
                style={{ marginTop: '20px' }}
              >
                ← Kembali ke Dashboard
              </Button>
            </EmptyState>
          </DashboardWrapper>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <DashboardWrapper>
          <DashboardHeader>
            <div>
              <Title>Dashboard Kasir</Title>
              <Subtitle>Verifikasi pembayaran booking yang masuk</Subtitle>
            </div>
            <Button
              variant="secondary"
              onClick={() => window.location.href = '/bus365/dashboard'}
              style={{ marginLeft: 'auto' }}
            >
              ← Kembali ke Dashboard
            </Button>
          </DashboardHeader>

          <StatsRow>
            <StatCard color="#27ae60">
              <StatLabel>Booking Approved</StatLabel>
              <StatValue>{approvedBookings.length}</StatValue>
            </StatCard>
            <StatCard color="#f39c12" style={{ position: 'relative' }}>
              <StatLabel>Pending Verifikasi</StatLabel>
              <StatValue>{stats.pending}</StatValue>
              {stats.pending > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: '#e74c3c',
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  animation: 'pulse 1.5s infinite',
                }}>!</span>
              )}
            </StatCard>
            <StatCard color="#e74c3c">
              <StatLabel>Awaiting Pelunasan</StatLabel>
              <StatValue>{awaitingPelunasan.length}</StatValue>
            </StatCard>
            <StatCard color="#3498db">
              <StatLabel>Total Nilai</StatLabel>
              <StatValue style={{ fontSize: '24px' }}>
                {formatCurrency(stats.totalAmount)}
              </StatValue>
            </StatCard>
          </StatsRow>

          {/* Info Banner - Workflow Explanation */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '15px 20px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            boxShadow: '0 2px 10px rgba(102, 126, 234, 0.3)',
          }}>
            <span style={{ fontSize: '28px' }}>💡</span>
            <div>
              <strong style={{ fontSize: '14px' }}>Alur Pembayaran:</strong>
              <div style={{ fontSize: '13px', marginTop: '5px', opacity: '0.95' }}>
                1️⃣ Upload bukti bayar → 2️⃣ <strong>Verifikasi di tabel "Pending Verifikasi" di bawah</strong> → 3️⃣ Status booking berubah otomatis
              </div>
            </div>
          </div>

          {/* MOVED TO TOP: Pending Verifications - Most Important! */}
          {stats.pending > 0 && (
            <TableContainer style={{ marginBottom: '20px', border: '2px solid #f39c12', boxShadow: '0 0 15px rgba(243, 156, 18, 0.2)' }}>
              <TableHeader style={{ background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)' }}>
                <TableTitle style={{ color: 'white' }}>⚠️ PERLU VERIFIKASI ({stats.pending})</TableTitle>
                <Button variant="view" onClick={fetchPendingVerifications} disabled={loading} style={{ background: 'white', color: '#f39c12' }}>
                  🔄 Refresh
                </Button>
              </TableHeader>

              {loading ? (
                <LoadingContainer>
                  <Spinner />
                </LoadingContainer>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>No. Booking</TableHeaderCell>
                      <TableHeaderCell>Customer</TableHeaderCell>
                      <TableHeaderCell>Jumlah</TableHeaderCell>
                      <TableHeaderCell>Tipe</TableHeaderCell>
                      <TableHeaderCell>Metode</TableHeaderCell>
                      <TableHeaderCell>Upload</TableHeaderCell>
                      <TableHeaderCell>Bukti</TableHeaderCell>
                      <TableHeaderCell>Aksi</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <tbody>
                    {pendingVerifications.map((verification) => (
                      <TableRow key={verification.id} style={{ background: '#fffbeb' }}>
                        <TableCell>
                          <BookingLink href={`/booking/${verification.booking_id}`} target="_blank">
                            {verification.booking_number}
                          </BookingLink>
                        </TableCell>
                        <TableCell>{verification.customer_name}</TableCell>
                        <TableCell>
                          <AmountText style={{ color: '#d35400', fontWeight: 'bold' }}>
                            {formatCurrency(verification.payment_amount)}
                          </AmountText>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={verification.payment_type === 'dp' ? 'pending' : 'approved'}>
                            {verification.payment_type === 'dp' ? 'DP' : verification.payment_type === 'pelunasan' ? 'Pelunasan' : verification.payment_type?.toUpperCase() || '-'}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>{verification.payment_method || '-'}</TableCell>
                        <TableCell style={{ fontSize: '12px' }}>
                          {verification.created_at ? formatDate(verification.created_at) : '-'}
                        </TableCell>
                        <TableCell>
                          {verification.payment_proof_file && (
                            <Button
                              variant="view"
                              onClick={() => {
                                const filename = verification.payment_proof_file.split('/').pop();
                                window.open(
                                  `${process.env.REACT_APP_BACKEND_URL}/api/payment-verification/file/${filename}`,
                                  '_blank'
                                );
                              }}
                            >
                              👁️ Lihat
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <ActionButtons>
                            <Button
                              variant="verify"
                              onClick={() => handleVerify(verification.id)}
                              disabled={actionLoading}
                              style={{ background: '#27ae60' }}
                            >
                              ✓ Verifikasi
                            </Button>
                            <Button
                              variant="reject"
                              onClick={() => handleRejectClick(verification)}
                              disabled={actionLoading}
                            >
                              ✗ Tolak
                            </Button>
                          </ActionButtons>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              )}
            </TableContainer>
          )}

          {/* Approved Bookings - Need Payment Upload */}
          <TableContainer style={{ marginBottom: '20px' }}>
            <TableHeader>
              <TableTitle>📋 Booking Approved - Perlu Upload Bukti Bayar</TableTitle>
              <Button variant="view" onClick={fetchApprovedBookings} disabled={approvedLoading}>
                🔄 Refresh
              </Button>
            </TableHeader>

            {approvedLoading ? (
              <LoadingContainer>
                <Spinner />
              </LoadingContainer>
            ) : approvedBookings.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>📭</EmptyStateIcon>
                <EmptyStateText>
                  Tidak ada booking approved yang menunggu pembayaran
                </EmptyStateText>
              </EmptyState>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>No. Booking</TableHeaderCell>
                    <TableHeaderCell>Customer</TableHeaderCell>
                    <TableHeaderCell>Rute</TableHeaderCell>
                    <TableHeaderCell>Tanggal</TableHeaderCell>
                    <TableHeaderCell>Harga Kontrak</TableHeaderCell>
                    <TableHeaderCell>DP</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Aksi</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <tbody>
                  {approvedBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <BookingLink href={`/booking/${booking.id}`} target="_blank">
                          {booking.booking_number}
                        </BookingLink>
                      </TableCell>
                      <TableCell>
                        {booking.customer_name}<br />
                        <small style={{ color: '#666' }}>{booking.customer_phone}</small>
                      </TableCell>
                      <TableCell>{booking.route || '-'}</TableCell>
                      <TableCell>
                        {new Date(booking.departure_date).toLocaleDateString('id-ID')}
                      </TableCell>
                      <TableCell>
                        <AmountText>{formatCurrency(booking.contract_price)}</AmountText>
                      </TableCell>
                      <TableCell>{formatCurrency(booking.dp_amount)}</TableCell>
                      <TableCell>
                        <StatusBadge status="approved">
                          {booking.status === 'approved' ? 'Approved' : 'Confirmed'}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="verify"
                          onClick={() => handleUploadClick(booking, false)}
                        >
                          📤 Upload Bukti Bayar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            )}
          </TableContainer>

          {/* Awaiting Pelunasan - DP Paid, Need Final Payment */}
          <TableContainer style={{ marginBottom: '20px' }}>
            <TableHeader>
              <TableTitle>💰 Awaiting Pelunasan - DP Sudah Dibayar</TableTitle>
              <Button variant="view" onClick={fetchAwaitingPelunasan} disabled={pelunasanLoading}>
                🔄 Refresh
              </Button>
            </TableHeader>

            {pelunasanLoading ? (
              <LoadingContainer>
                <Spinner />
              </LoadingContainer>
            ) : awaitingPelunasan.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>✅</EmptyStateIcon>
                <EmptyStateText>
                  Tidak ada booking yang menunggu pelunasan
                </EmptyStateText>
              </EmptyState>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>No. Booking</TableHeaderCell>
                    <TableHeaderCell>Customer</TableHeaderCell>
                    <TableHeaderCell>Rute</TableHeaderCell>
                    <TableHeaderCell>Tanggal</TableHeaderCell>
                    <TableHeaderCell>Harga Kontrak</TableHeaderCell>
                    <TableHeaderCell>Sudah Dibayar</TableHeaderCell>
                    <TableHeaderCell>Sisa</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Aksi</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <tbody>
                  {awaitingPelunasan.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <BookingLink href={`/booking/${booking.id}`} target="_blank">
                          {booking.booking_number}
                        </BookingLink>
                      </TableCell>
                      <TableCell>
                        {booking.customer_name}<br />
                        <small style={{ color: '#666' }}>{booking.customer_phone}</small>
                      </TableCell>
                      <TableCell>{booking.route || '-'}</TableCell>
                      <TableCell>
                        {new Date(booking.departure_date).toLocaleDateString('id-ID')}
                      </TableCell>
                      <TableCell>
                        <AmountText>{formatCurrency(booking.contract_price)}</AmountText>
                      </TableCell>
                      <TableCell>
                        <span style={{ color: '#27ae60', fontWeight: 'bold' }}>
                          {formatCurrency(booking.total_verified)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                          {formatCurrency(booking.remaining_amount)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status="dp_paid">
                          DP Paid
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="verify"
                          onClick={() => handleUploadClick(booking, true)}
                        >
                          💵 Upload Pelunasan
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            )}
          </TableContainer>

          {/* Show empty state only when no pending verifications */}
          {stats.pending === 0 && (
            <TableContainer style={{ marginBottom: '20px' }}>
              <TableHeader>
                <TableTitle>✅ Verifikasi Pembayaran</TableTitle>
                <Button variant="view" onClick={fetchPendingVerifications} disabled={loading}>
                  🔄 Refresh
                </Button>
              </TableHeader>
              <EmptyState>
                <EmptyStateIcon>✅</EmptyStateIcon>
                <EmptyStateText>
                  Semua pembayaran sudah diverifikasi! Tidak ada yang pending.
                </EmptyStateText>
              </EmptyState>
            </TableContainer>
          )}

          {showRejectModal && (
            <Modal onClick={handleCloseModal}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                  <ModalTitle>Tolak Bukti Pembayaran</ModalTitle>
                </ModalHeader>
                <ModalBody>
                  <p>
                    <strong>Booking:</strong> {selectedVerification?.booking_number}
                    <br />
                    <strong>Customer:</strong> {selectedVerification?.customer_name}
                    <br />
                    <strong>Jumlah:</strong> {formatCurrency(selectedVerification?.payment_amount || 0)}
                  </p>
                  <FormGroup>
                    <Label>Alasan Penolakan *</Label>
                    <TextArea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Jelaskan alasan penolakan bukti pembayaran..."
                      disabled={actionLoading}
                    />
                  </FormGroup>
                </ModalBody>
                <ModalFooter>
                  <CancelButton onClick={handleCloseModal} disabled={actionLoading}>
                    Batal
                  </CancelButton>
                  <SubmitButton onClick={handleRejectSubmit} disabled={actionLoading || !rejectionReason.trim()}>
                    {actionLoading ? 'Memproses...' : 'Tolak Pembayaran'}
                  </SubmitButton>
                </ModalFooter>
              </ModalContent>
            </Modal>
          )}

          {/* Upload Payment Proof Modal */}
          {showUploadModal && (
            <Modal onClick={handleCloseUploadModal}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                  <ModalTitle>Upload Bukti Pembayaran</ModalTitle>
                </ModalHeader>
                <ModalBody>
                  <p>
                    <strong>Booking:</strong> {selectedBooking?.booking_number}
                    <br />
                    <strong>Customer:</strong> {selectedBooking?.customer_name}
                    <br />
                    <strong>Harga Kontrak:</strong> {formatCurrency(selectedBooking?.contract_price || 0)}
                    <br />
                    {selectedBooking?.total_verified > 0 && (
                      <>
                        <strong>Sudah Dibayar:</strong> <span style={{ color: '#27ae60' }}>{formatCurrency(selectedBooking?.total_verified || 0)}</span>
                        <br />
                        <strong>Sisa:</strong> <span style={{ color: '#e74c3c' }}>{formatCurrency(selectedBooking?.remaining_amount || 0)}</span>
                        <br />
                      </>
                    )}
                    <strong>DP:</strong> {formatCurrency(selectedBooking?.dp_amount || 0)}
                  </p>
                  <FormGroup>
                    <Label>Jenis Pembayaran *</Label>
                    <select
                      value={uploadData.payment_type}
                      onChange={(e) => setUploadData({ ...uploadData, payment_type: e.target.value })}
                      disabled={actionLoading}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                    >
                      <option value="dp">Down Payment (DP)</option>
                      <option value="pelunasan">Pelunasan</option>
                      <option value="full">Pembayaran Penuh</option>
                      <option value="other">Lainnya</option>
                    </select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Jumlah Pembayaran *</Label>
                    <input
                      type="number"
                      value={uploadData.payment_amount}
                      onChange={(e) => setUploadData({ ...uploadData, payment_amount: e.target.value })}
                      placeholder="Masukkan jumlah pembayaran"
                      disabled={actionLoading}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Metode Pembayaran</Label>
                    <select
                      value={uploadData.payment_method}
                      onChange={(e) => setUploadData({ ...uploadData, payment_method: e.target.value })}
                      disabled={actionLoading}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                    >
                      <option value="transfer">Transfer Bank</option>
                      <option value="cash">Cash</option>
                      <option value="qris">QRIS</option>
                      <option value="other">Lainnya</option>
                    </select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Bukti Pembayaran * (JPG, PNG, PDF max 5MB)</Label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => setUploadData({ ...uploadData, payment_proof: e.target.files[0] })}
                      disabled={actionLoading}
                      style={{ width: '100%', padding: '8px' }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Catatan</Label>
                    <TextArea
                      value={uploadData.notes}
                      onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
                      placeholder="Catatan tambahan (opsional)"
                      disabled={actionLoading}
                    />
                  </FormGroup>
                </ModalBody>
                <ModalFooter>
                  <CancelButton onClick={handleCloseUploadModal} disabled={actionLoading}>
                    Batal
                  </CancelButton>
                  <SubmitButton onClick={handleUploadSubmit} disabled={actionLoading || !uploadData.payment_proof}>
                    {actionLoading ? 'Mengupload...' : 'Upload Bukti Bayar'}
                  </SubmitButton>
                </ModalFooter>
              </ModalContent>
            </Modal>
          )}
        </DashboardWrapper>
      </Container>
    </Layout>
  );
};

export default KasirDashboard;
